export const METODOS = ["AGENCIA", "RETIRA", "CAMION", "GIRA"];

// Un "pedido de componentes de kit" es el que los vendedores marcan en la
// descripción. Sirve solo de referencia para armar el kit — NO se prepara,
// asigna, despacha ni notifica.
//
// Los vendedores no escriben siempre igual: aparecen "COMPONENTES KIT",
// "COMPONENTES KITS" y "COMPOSICION KIT". Esta regex tolera esas variantes (y el
// acento en COMPOSICIÓN). Se exporta para que KitVinculo use el MISMO criterio.
export const RE_MARCADOR_COMPONENTES = /COMP(?:ONENTES?|OSICI[OÓ]N)\s+KITS?/;

export function textoPedido(p) {
  return [p?.descripcion, p?.observaciones, p?.nota, p?.comentario, p?.detalle]
    .filter(Boolean).join(" ").toUpperCase();
}

export function esPedidoComponentes(p) {
  if (!p) return false;
  return RE_MARCADOR_COMPONENTES.test(textoPedido(p));
}

// Segunda forma de identificar un pedido de componentes: el pedido que tiene el
// KIT referencia al de componentes con "REF. N" en la descripción de sus productos
// (ej. producto "503000004 Kit 6 Puertas · REF. 23206" → 23206 es el de componentes).
// Extrae los números referenciados en los productos de UN pedido.
export function numerosRefEnPedido(p) {
  const out = new Set();
  for (const it of (p?.productos || [])) {
    const txt = String(it?.desc || it?.descripcion || it?.nombre || "");
    const re = /REF\.?\s*#?\s*(\d{4,6})\b/gi;
    let m;
    while ((m = re.exec(txt))) out.add(m[1]);
    // El vendedor suele poner el N de pedido de componentes en la DESCRIPCION del
    // item del Kit (campo DESCITEM) como numero suelto (ej: "23674"). Lo tomamos como
    // referencia SOLO para items Kit (evita confundir numeros sueltos de otros items).
    if (/\bkit\b/i.test(txt)) {
      const di = String(it?.descItem || "").match(/\b(\d{4,6})\b/);
      if (di) out.add(di[1]);
    }
  }
  return out;
}

// A partir de TODOS los pedidos, arma el set de números que son "componentes de
// kit" porque algún kit los referencia con "REF. N".
export function numerosComponentesReferenciados(pedidos) {
  const set = new Set();
  for (const p of (pedidos || [])) {
    for (const n of numerosRefEnPedido(p)) set.add(n);
  }
  return set;
}

// ¿Es un pedido de componentes? Por descripción ("COMPONENTES KIT") o porque su
// número está referenciado por un kit ("REF. N"). `referenciados` es opcional
// (un Set de números, de numerosComponentesReferenciados).
export function esPedidoComponentesExt(p, referenciados) {
  if (esPedidoComponentes(p)) return true;
  if (referenciados && p?.numero != null && referenciados.has(String(p.numero))) return true;
  return false;
}

// ¿El pedido tiene un Kit que referencia un pedido de componentes (REF. N) pero NO
// quedó vinculado? → señal de alerta para Ventas/Encargado.
// Un kit vinculado guarda un snapshot en `pedido.kitComponentes[codigoKit]`; si el
// pedido referencia componentes (REF. N) y no hay snapshot, el vínculo no se hizo.
// Los kits predefinidos no referencian componentes (se expanden solos) → no entran acá.
export function kitSinVincular(p) {
  if (!p) return false;
  const refs = numerosRefEnPedido(p);
  if (!refs || refs.size === 0) return false; // no referencia componentes → nada que vincular
  const kc = p.kitComponentes;
  const vinculado = kc && typeof kc === "object" && Object.keys(kc).length > 0;
  return !vinculado;
}

/** Lista canónica de agencias en el orden de carga al camión */
export const AGENCIAS = [
  { nombre: "EL CALABRES", orden: 1,  alias: ["EL CALABRES", "CALABRES"] },
  { nombre: "VENEZUELA",   orden: 2,  alias: ["VENEZUELA"] },
  { nombre: "ASTRANS",     orden: 3,  alias: ["ASTRANS"] },
  { nombre: "ECHENIQUE",   orden: 4,  alias: ["ECHENIQUE"] },
  { nombre: "ARIEL",       orden: 5,  alias: ["ARIEL"] },
  { nombre: "BERRO",       orden: 6,  alias: ["BERRO"] },
  { nombre: "ACC",         orden: 7,  alias: ["ACC"] },
  { nombre: "TRUJILLO",    orden: 8,  alias: ["TRUJILLO"] },
  { nombre: "TAMER",       orden: 9,  alias: ["TAMER"] },
  { nombre: "CUAREIM",     orden: 10, alias: ["CUAREIM"] },
  { nombre: "YI",          orden: 11, alias: ["\\bYI\\b"] },
  { nombre: "REPUBLICA",   orden: 12, alias: ["REPUBLICA"] },
  { nombre: "EL NORTEÑO",  orden: 13, alias: ["EL NORTENO", "NORTENO"] },
  { nombre: "TURIL",       orden: 14, alias: ["TURIL"] },
  { nombre: "NORESTE",     orden: 15, alias: ["NORESTE"] },
  { nombre: "DAC",         orden: 16, alias: ["\\bDAC\\b"] },
];

/**
 * Extrae el destino y/o fecha de salida que viene después del keyword "CAMION".
 * Soporta formatos:
 *   "CAMION MALDONADO"
 *   "CAMION 15/04"
 *   "CAMION 15/04/2026"
 *   "CAMION MALDONADO 15/04"
 *   "CAMION RIVERA 15-04-2026"
 * Retorna { destino: string|null, fechaStr: "YYYY-MM-DD"|null }
 */
export function detectarInfoCamion(norm = "") {
  const match = norm.match(/CAMION\s+(.*)/);
  if (!match) return { destino: null, fechaStr: null };

  let resto = match[1].trim();
  if (!resto) return { destino: null, fechaStr: null };

  let fechaStr = null;

  // Intentar extraer fecha: dd/mm, dd/mm/yy, dd/mm/yyyy, dd-mm-yyyy
  const dateRe = /(\d{1,2})[\/\-](\d{1,2})(?:[\/\-](\d{2,4}))?/;
  const dm = resto.match(dateRe);
  if (dm) {
    const day   = dm[1].padStart(2, "0");
    const month = dm[2].padStart(2, "0");
    let   year  = dm[3];
    if (year) {
      year = year.length === 2 ? `20${year}` : year;
    } else {
      year = String(new Date().getFullYear());
    }
    fechaStr = `${year}-${month}-${day}`;
    resto = resto.replace(dm[0], "").trim();
  }

  // Lo que queda es el destino (solo letras y números)
  const destino = resto.replace(/[^A-Z0-9\s]/g, "").trim() || null;

  return { destino: destino || null, fechaStr };
}

/** Intenta detectar la agencia a partir del texto normalizado (sin acentos, en mayúsculas) */
export function detectarAgencia(norm = "") {
  // Intentar primero en el texto que sigue después de "AGENCIA"
  const afterKeyword = norm.match(/AGENCIA\s+(.*)/);
  const searchIn = afterKeyword ? afterKeyword[1] : norm;

  for (const ag of AGENCIAS) {
    for (const alias of ag.alias) {
      // Soporta regex escapado (ej: \bYI\b) o texto plano
      const re = alias.startsWith("\\") ? new RegExp(alias) : new RegExp(`\\b${alias}\\b`);
      if (re.test(searchIn)) return ag.nombre;
    }
  }
  return null;
}

/**
 * Detecta el número de obra PVC en la descripción del pedido.
 * Soporta "Obra 198", "OBRA N° 198", "obra-198", "Obra Nro. 198", etc.
 * Retorna el número (int) o null.
 */
export function extraerObraNumero(norm = "") {
  const m = norm.match(/\bOBRA\b[^0-9]{0,8}(\d{1,6})/);
  return m ? Number(m[1]) : null;
}

/**
 * Parsea la descripción de Finnegans y devuelve { deposito, metodoEntrega, raw, esCotizacion }.
 * - Soporta formatos “R8 - CAMION”, “ISABELA-CAMION”, “ISA - GIRA”,
 *   “DEPÓSITO: R8 / MÉTODO: CAMION”, etc.
 * - Normaliza acentos, guiones y mayúsculas.
 * - Aplica defaults de negocio:
 *    • AGENCIA  -> deposito R8 (si no viene)
 *    • R8 se detecta como "R8", "RUTA 8" o "RUTA8"
 *    • Si no matchea R8 ni ISABELA -> SIN depósito (null) para asignación manual
 *    • ISABELA también se detecta como "ISA"
 * - Si la descripción contiene "COTIZACION", se marca esCotizacion = true
 */
export function parseDescripcionPedido(descRaw = "") {
  const desc = String(descRaw ?? "").trim();
  if (!desc) return { deposito: null, metodoEntrega: null, raw: descRaw, esCotizacion: false };

  const norm = desc
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // quita acentos
    .replace(/[–—−]/g, "-")
    .replace(/\s*-\s*/g, " - ")
    .toUpperCase();

  // 🔹 0) Detectar COTIZACION
  const esCotizacion = /\bCOTI[ZC]ACION\b/.test(norm) || norm.startsWith("COTIZ");

  let deposito = null;
  let metodoEntrega = null;
  let agencia = null;

  // 1) Intento de formato "DEP - METODO"
  const parts = norm.split(" - ");
  if (parts.length === 2) {
    const [a, b] = parts.map(s => s.trim());

    // detecta depósito por palabra clave (incluye ISA como alias de ISABELA)
    if (/^(ISABELA|ISA)$/.test(a) || /DEP(OSI|OSITO)?\s*(ISABELA|ISA)\b/.test(a)) {
      deposito = "ISABELA";
    } else if (/^(R8|RUTA\s*8)$/.test(a) || /DEP(OSI|OSITO)?\s*R8\b/.test(a)) {
      deposito = "R8";
    }

    if (METODOS.includes(b)) metodoEntrega = b;
  }

  // 2) Si no se pudo por guión, buscar keywords sueltas
  if (!deposito || !metodoEntrega) {
    if (!deposito) {
      if (/ISABELA|^ISA\b/.test(norm)) deposito = "ISABELA";
      else if (/\bR8\b|\bRUTA\s*8\b|DEP(OSI|OSITO)?\s*R8\b/.test(norm)) deposito = "R8";
    }
    if (!metodoEntrega) {
      const met = METODOS.find(m => norm.includes(m));
      if (met) metodoEntrega = met;
    }
  }

  // 3) Defaults de negocio
  //    - Si método AGENCIA => R8 (si no vino depósito)
  if (!deposito) {
    if (metodoEntrega === "AGENCIA") deposito = "R8";
  }
  // Los pedidos de componentes de kit (COMPOSICION/COMPONENTES KIT) son de R8:
  // se vinculan a un kit de R8. Sin esto quedarian sin deposito y la vinculacion
  // (que busca por deposito R8) no los encontraria.
  if (!deposito && /COMP(?:ONENTES?|OSICI[O\u00d3]N)\s+KITS?/.test(norm)) deposito = "R8";

  // 4) Si aún falta depósito, default R8
  // Sin default a R8: si no matchea R8/RUTA 8 ni ISABELA, queda SIN deposito
  // (deposito = null) y cae en "Pedidos sin deposito" para que Ventas lo asigne.

  // 5) Sanitizar salida
  if (metodoEntrega && !METODOS.includes(metodoEntrega)) metodoEntrega = null;

  // 6) Detectar agencia si el método es AGENCIA
  if (metodoEntrega === "AGENCIA") {
    agencia = detectarAgencia(norm);
  }

  // 7) Detectar destino/fecha de camión si el método es CAMION
  let camionDestino = null;
  let camionFechaStr = null;
  if (metodoEntrega === "CAMION") {
    const info = detectarInfoCamion(norm);
    camionDestino  = info.destino;
    camionFechaStr = info.fechaStr;
  }

  // 8) Detectar número de obra PVC ("Obra 198")
  const obraNumero = extraerObraNumero(norm);

  // 9) Código de viaje entre paréntesis: "GIRA (MALDO25)" / "CAMION (RIV12)".
  //    Sirve para Gira y Camión. Se usa para auto-asignar el pedido al viaje que
  //    tenga ese código. Se normaliza a letras+dígitos en mayúscula.
  let camionCodigo = null;
  if (metodoEntrega === "GIRA" || metodoEntrega === "CAMION") {
    const m = norm.match(/\(([^)]+)\)/);
    if (m) camionCodigo = m[1].replace(/[^A-Z0-9]/g, "").trim() || null;
  }

  return {
    deposito:      deposito || null,
    metodoEntrega: metodoEntrega || null,
    agencia:       agencia || null,
    camionDestino: camionDestino || null,
    camionFechaStr: camionFechaStr || null,
    camionCodigo:  camionCodigo || null,
    obraNumero:    obraNumero || null,
    raw:           descRaw,
    esCotizacion,
  };
}
